<template>
	<div
		class="echo-form bg-white rounded-xl shadow-sm border border-gray-200 p-2 print:border-0 print:shadow-none print:rounded-none ">
		<h1 class="mb-2 text-center text-xl font-bold text-gray-800 tracking-tight">
			ECOCARDIOGRAFIE TRANSTORACICĂ
		</h1>

		<div class="grid grid-cols-1 md:grid-cols-3 gap-4">
			<!-- Column 1 - Left Ventricle -->
			<div class="space-y-2">
				<div
					class="bg-gradient-to-r from-rose-50 to-rose-100 px-4 py-2 rounded-lg border-l-4 border-rose-600 print:hidden">
					<h3 class="font-bold text-sm text-rose-900 uppercase tracking-wide">
						Ventricul Stâng
					</h3>
				</div>
				<div class="space-y-1 pl-2">
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'dtdvs-input'" label="DTDVS (mm)" field="DTDVS" :sex="sexPacient" :value="DTDVS"
							@update:value="$emit('update:DTDVS', $event)" :inputClass="dtdvsClass" :showSecond="true"
							:secondValue="DTDVSbsa" :secondClass="dtdvsbsaClass" :secondDisabled="true" unit="mm/m²"
							:readonly="false" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'dtsvs-input'" label="DTSVS" field="DTSVS" :sex="sexPacient" :value="DTSVS"
							@update:value="$emit('update:DTSVS', $event)" :inputClass="dtsvsClass" :showSecond="true"
							:secondValue="DTSVSbsa" :secondClass="dtsvsbsaClass" :secondDisabled="true" unit="mm/m²"
							:readonly="false" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'sivd-input'" label="SIVd" field="SIVd" :sex="sexPacient" :value="SIVd"
							@update:value="$emit('update:SIVd', $event)" :inputClass="sivdClass" :readonly="false"
							:disabled="disabled" unit="mm/m²" :showInfo="false" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'ppvsd-input'" label="PPVSd" field="PPVSd" :sex="sexPacient" :value="PPVSd"
							@update:value="$emit('update:PPVSd', $event)" :inputClass="ppvsdClass" :readonly="false"
							:disabled="disabled" unit="mm/m²" :showInfo="false" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'masavs-input'" label="MasaVS" field="MasaVS" :sex="sexPacient" :value="MasaVS"
							@update:value="$emit('update:MasaVS', $event)" :inputClass="masavsClass" unit="g/m²"
							:readonly="true" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'rtw-input'" label="RTW" field="RTW" :sex="sexPacient" :value="RTW"
							@update:value="$emit('update:RTW', $event)" :inputClass="rtwClass" unit="g/m²"
							:readonly="true" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'fs-input'" label="FS" field="FS" :sex="sexPacient" :value="FS"
							@update:value="$emit('update:FS', $event)" :inputClass="fsClass" unit="%" :readonly="false"
							:showInfo="false" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'fe-input'" label="FE" field="FE" :sex="sexPacient" :value="FE"
							@update:value="$emit('update:FE', $event)" :inputClass="feClass" unit="%" :readonly="false"
							:showInfo="false" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'fevol-input'" field="FEvol" :sex="sexPacient" :value="FEvol"
							@update:value="$emit('update:FEvol', $event)" :inputClass="fevolClass" unit="%"
							:readonly="false" :showInfo="false" :disabled="disabled">
							<template #label>
								FE<sub>vol</sub>
							</template>
						</EchoFieldRow>
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'mapse-input'" label="MAPSE" field="MAPSE" :sex="sexPacient" :value="MAPSE"
							@update:value="$emit('update:MAPSE', $event)" :inputClass="mapseClass" :readonly="false"
							:showInfo="false" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'vtdvs-input'" label="VTDVS" field="VTDVS" :sex="sexPacient" :value="VTDVS"
							@update:value="$emit('update:VTDVS', $event)" :inputClass="vtdvsClass" :showSecond="true"
							:secondValue="VTDVSbsa" :secondClass="vtdvsbsaClass" :secondDisabled="true" unit="ml/m²"
							:readonly="false" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow :data-cy="'vtsvs-input'" label="VTSVS" field="VTSVS" :sex="sexPacient" :value="VTSVS"
							@update:value="$emit('update:VTSVS', $event)" :inputClass="vtsvsClass" :showSecond="true"
							:secondValue="VTSVSbsa" :secondClass="vtsvsbsaClass" :secondDisabled="true" unit="ml/m²"
							:readonly="false" :disabled="disabled" />
					</div>
				</div>
			</div>

			<!-- Column 2 - Mitral Valve -->
			<div class="space-y-2">
				<div
					class="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 rounded-lg border-l-4 border-blue-600 print:hidden">
					<h3 class="font-bold text-sm text-blue-900 uppercase tracking-wide">
						Valva Mitrală
					</h3>
				</div>
				<div class="space-y-1 pl-2">
					<div class="flex gap-2 items-center">
						<EchoFieldRow data-cy="dpmaxmitral-input" field="DPmaxMitral" :sex="sexPacient" :value="DPmaxMitral"
							@update:value="$emit('update:DPmaxMitral', $event)" unit="mmHg" :readonly="false"
							:showInfo="false" :disabled="disabled">
							<template #label>
								ΔP<sub>max</sub>
							</template>
						</EchoFieldRow>
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow data-cy="dpmediumitral-input" field="DPmediuMitral" :sex="sexPacient" :value="DPmediuMitral"
							@update:value="$emit('update:DPmediuMitral', $event)" unit="mmHg" :readonly="false"
							:showInfo="false" :disabled="disabled">
							<template #label>
								ΔP<sub>mediu</sub>
							</template>
						</EchoFieldRow>
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow data-cy="vmaxmitral-input" field="VmaxMitral" :sex="sexPacient" :value="VmaxMitral"
							@update:value="$emit('update:VmaxMitral', $event)" unit="m/s" :readonly="false"
							:showInfo="false" :disabled="disabled">
							<template #label>
								V<sub>max</sub>
							</template>
						</EchoFieldRow>
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow data-cy="sommitral-input" label="SOM" field="SOMMitral" :sex="sexPacient" :value="SOMMitral"
							@update:value="$emit('update:SOMMitral', $event)" unit="cm²" :readonly="false"
							:showInfo="false" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow data-cy="phtmitral-input" label="PHT" field="PHTMitral" :sex="sexPacient" :value="PHTMitral"
							@update:value="$emit('update:PHTMitral', $event)" unit="ms" :readonly="false"
							:showInfo="false" :disabled="disabled" />
					</div>
					<div class="flex gap-2 items-center">
						<EchoFieldRow data-cy="som20mitral-input" field="SOM20Mitral" :sex="sexPacient" :value="SOM20Mitral"
							@update:value="$emit('update:SOM20Mitral', $event)" unit="cm²" :readonly="false"
							:showInfo="false" :disabled="disabled">
							<template #label>
								SOM<sub>20</sub>
							</template>
						</EchoFieldRow>
					</div>
					<div class="flex gap-2 items-center print:mb-4">
						<EchoFieldRow data-cy="dpdtmitral-input" label="dp/dt" field="dpdtMitral" :sex="sexPacient" :value="dpdtMitral"
							@update:value="$emit('update:dpdtMitral', $event)" unit="cm²" :readonly="false"
							:showInfo="false" :disabled="disabled" />
					</div>
				</div>
			</div>
			<!-- Column 3 - Conclusions -->
			<div class="space-y-3">
				<div
					class="bg-gradient-to-r from-green-50 to-green-100 px-4 py-2 rounded-lg border-l-4 border-green-600 print:hidden">
					<h3 class="font-bold text-sm text-green-900 uppercase tracking-wide">
						Concluzii
					</h3>
				</div>
				<!-- Predefined observations list -->
				<div class="print:hidden space-y-1">
					<div class="bg-gray-50 rounded-lg border border-gray-200 p-3 max-h-50 overflow-y-auto">
						<div class="space-y-1">
							<div data-cy="observation-item" v-for="(obs, index) in observatii" :key="index"
								@click="!disabled && $emit('addObservation', obs)"
								class="text-s p-1 rounded transition-all duration-150 border border-transparent" :class="disabled
									? 'text-gray-500 cursor-not-allowed'
									: 'hover:bg-white hover:shadow-sm cursor-pointer hover:border-green-200'" v-html="obs"></div>
						</div>
					</div>
				</div>
				<Textarea data-cy="concluzii-input" :modelValue="concluzii" @update:modelValue="$emit('update:concluzii', $event)" rows="13"
					class="w-full print:hidden" placeholder="Introduceți concluziile..." :disabled="disabled" />
				<div class="print-only">
					<b>Concluzii</b>
					<div v-html="concluziiFormatted"></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from "vue";
import Textarea from "primevue/textarea";
import EchoFieldRow from "../ui/EchoFieldRow.vue";

const props = defineProps({
	sexPacient: String,
	DTDVS: String,
	DTDVSbsa: String,
	DTSVS: String,
	DTSVSbsa: String,
	SIVd: String,
	PPVSd: String,
	MasaVS: String,
	RTW: String,
	FS: String,
	FE: String,
	FEvol: String,
	MAPSE: String,
	VTDVS: String,
	VTDVSbsa: String,
	VTSVS: String,
	VTSVSbsa: String,
	DPmaxMitral: String,
	DPmediuMitral: String,
	VmaxMitral: String,
	SOMMitral: String,
	PHTMitral: String,
	SOM20Mitral: String,
	dpdtMitral: String,
	concluzii: { type: String, default: "" },
	observatii: { type: Array, default: () => [] },
	getValidationClass: { type: Function, default: () => "" },

	disabled: {
		type: Boolean,
		default: false
	}
});

defineEmits([
	"update:DTDVS",
	"update:DTDVSbsa",
	"update:DTSVS",
	"update:DTSVSbsa",
	"update:SIVd",
	"update:PPVSd",
	"update:MasaVS",
	"update:RTW",
	"update:FS",
	"update:FE",
	"update:FEvol",
	"update:MAPSE",
	"update:VTDVS",
	"update:VTDVSbsa",
	"update:VTSVS",
	"update:VTSVSbsa",
	"update:DPmaxMitral",
	"update:DPmediuMitral",
	"update:VmaxMitral",
	"update:SOMMitral",
	"update:PHTMitral",
	"update:SOM20Mitral",
	"update:dpdtMitral",
	"update:concluzii",
	"addObservation",
]);

const concluziiFormatted = computed(() => {
	return (props.concluzii || "").replace(/\n/g, "<br/>");
});

// Individual computed properties for each validated field to ensure reactivity
const dtdvsClass = computed(() => props.getValidationClass?.("DTDVS") || "");
const dtdvsbsaClass = computed(
	() => props.getValidationClass?.("DTDVSbsa") || ""
);
const dtsvsClass = computed(() => props.getValidationClass?.("DTSVS") || "");
const dtsvsbsaClass = computed(
	() => props.getValidationClass?.("DTSVSbsa") || ""
);
const sivdClass = computed(() => props.getValidationClass?.("SIVd") || "");
const ppvsdClass = computed(() => props.getValidationClass?.("PPVSd") || "");
const masavsClass = computed(() => props.getValidationClass?.("MasaVS") || "");
const rtwClass = computed(() => props.getValidationClass?.("RTW") || "");
const fsClass = computed(() => props.getValidationClass?.("FS") || "");
const feClass = computed(() => props.getValidationClass?.("FE") || "");
const fevolClass = computed(() => props.getValidationClass?.("FEvol") || "");
const mapseClass = computed(() => props.getValidationClass?.("MAPSE") || "");
const vtdvsClass = computed(() => props.getValidationClass?.("VTDVS") || "");
const vtdvsbsaClass = computed(
	() => props.getValidationClass?.("VTDVSbsa") || ""
);
const vtsvsClass = computed(() => props.getValidationClass?.("VTSVS") || "");
const vtsvsbsaClass = computed(
	() => props.getValidationClass?.("VTSVSbsa") || ""
);
</script>
